import type { BeforeSendFn, CaptureResult } from "posthog-js";

const IGNORED_EXCEPTION_MESSAGES = ["Java object is gone"];

/** Events kept without statistics consent: page views, to count visitors. */
const COOKIELESS_EVENTS = new Set(["$pageview", "$pageleave"]);

const SKIPPED_BOOK_PROPERTIES = [
  "skip_type",
  "batch_code",
  "book_title",
  "skus",
];

const ANONYMOUS_EVENT_PROPERTIES: Record<string, string[]> = {
  subscription_renewal_skipped: SKIPPED_BOOK_PROPERTIES,
  subscription_renewal_skip_reason: [...SKIPPED_BOOK_PROPERTIES, "reason"],
  subscription_renewal_skip_comment: [...SKIPPED_BOOK_PROPERTIES, "comment"],
};

const POSTHOG_PROPERTIES = new Set(["token", "distinct_id"]);

/** Public paths whose next segment is an order, contract, cart or profile id. */
const CUSTOMER_ID_IN_PATH =
  /(\/(?:min-side\/(?:ordre|abonnement|samtykker)|kvittering|bekreftelse)\/)([^/?#]+)/g;

const URL_PROPERTIES = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$prev_pageview_pathname",
];

/**
 * Without consent PostHog captures in cookieless mode, and those events must
 * not be linkable to a customer. Other events are dropped: tracker events carry
 * the long-lived `cd_cid` client id and order ids, and autocapture can carry
 * page text. The anonymous skip events keep only their listed properties.
 * Ids in URLs and the page title are masked.
 */
function toCookielessEvent(event: CaptureResult): CaptureResult | null {
  const anonymousProperties = ANONYMOUS_EVENT_PROPERTIES[event.event];
  if (anonymousProperties) {
    for (const key of Object.keys(event.properties)) {
      const isKept =
        key.startsWith("$") ||
        POSTHOG_PROPERTIES.has(key) ||
        anonymousProperties.includes(key);
      if (!isKept) delete event.properties[key];
    }
  } else if (!COOKIELESS_EVENTS.has(event.event)) {
    return null;
  }

  const ids = new Set<string>();
  for (const key of URL_PROPERTIES) {
    const value = event.properties[key];
    if (typeof value !== "string") continue;
    event.properties[key] = value.replace(
      CUSTOMER_ID_IN_PATH,
      (_match, prefix: string, id: string) => {
        ids.add(id);
        return `${prefix}:id`;
      },
    );
  }

  const title = event.properties.title;
  if (typeof title === "string") {
    event.properties.title = [...ids].reduce(
      (masked, id) => masked.replaceAll(id, ":id"),
      title,
    );
  }

  return event;
}

/**
 * Filters PostHog events immediately before they are sent.
 *
 * Returning `null` suppresses an event. Cookieless events are reduced to page
 * views, see `toCookielessEvent`. "Java object is gone" originates from
 * third-party scripts and does not indicate an application error, so those
 * exceptions are discarded while every other event is returned unchanged.
 *
 * @see https://posthog.com/docs/error-tracking/capture#suppressing-exceptions
 */
export const beforeSend: BeforeSendFn = (event) => {
  if (event?.properties.$cookieless_mode === true) {
    return toCookielessEvent(event);
  }
  if (event?.event !== "$exception") return event;

  const exceptionList = event.properties["$exception_list"];
  const hasIgnoredMessage =
    Array.isArray(exceptionList) &&
    exceptionList.some(
      (exception) =>
        typeof exception?.value === "string" &&
        IGNORED_EXCEPTION_MESSAGES.some((message) =>
          exception.value.includes(message),
        ),
    );

  return hasIgnoredMessage ? null : event;
};
